import React, { useEffect, useState } from 'react'
import { Box, Container, SimpleGrid, Loader, Center, Group } from '@mantine/core'
import { AppBadge } from '../../app/common/ui'
import { AppButton, AppText, AppStack, AppInput } from '../../app/common/ui'
import { useDebouncedValue } from '@mantine/hooks'
import agent from '../../app/api/agent'
import { CartridgeSpecDto, AmmoProductDto } from '../../app/models/ammo'
import { PagedResult } from '../../app/models/pagedResult'
import { useHistory } from 'react-router-dom'
import { useStore } from '../../app/stores/store'
import { observer } from 'mobx-react-lite'

const AMMO_TYPES = ['Pistol', 'Rifle', 'Shotgun', 'Rimfire']

const CartridgeSpecCard: React.FC<{
  spec: CartridgeSpecDto;
  isSelected: boolean;
  productCount?: number;
  onClick: () => void;
}> = ({ spec, isSelected, productCount, onClick }) => (
  <Box
    onClick={onClick}
    sx={(theme) => ({
      padding: '12px',
      borderRadius: '6px',
      border: isSelected ? '2px solid var(--gs-ammo-border)' : '1px solid var(--gs-border)',
      background: isSelected ? 'var(--gs-surface2)' : 'var(--gs-surface)',
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        borderColor: 'var(--gs-ammo-border)',
        background: 'var(--gs-surface2)'
      }
    })}
  >
    <AppText weight={700} size="sm" sx={{ marginBottom: 'var(--gs-xs)' }}>
      {spec.name}
    </AppText>
    <AppText size="xs" sx={{ color: 'var(--gs-muted)', fontFamily: 'JetBrains Mono, monospace' }}>
      {spec.shortName} • {spec.caliber}
    </AppText>
    {productCount !== undefined && (
      <AppBadge
        size="sm"
        sx={{ marginTop: 'var(--gs-sm)', background: 'var(--gs-gold)' }}
        color="yellow"
      >
        {productCount} loads
      </AppBadge>
    )}
  </Box>
)

const AmmoProductCard: React.FC<{
  product: AmmoProductDto;
  onClick: () => void;
}> = ({ product, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      padding: '12px',
      borderRadius: '6px',
      border: '1px solid var(--gs-border)',
      background: 'var(--gs-surface)',
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        borderColor: 'var(--gs-ammo-border)',
        background: 'var(--gs-surface2)'
      }
    }}
  >
    <AppText weight={700} size="sm" sx={{ marginBottom: 'var(--gs-xs)' }}>
      {product.brand} {product.name}
    </AppText>
    <AppText size="xs" sx={{ color: 'var(--gs-text)', fontFamily: 'JetBrains Mono, monospace' }}>
      {product.grainWeight}gr {product.bulletType}
    </AppText>
  </Box>
)

const AmmoDashboard = observer(() => {
  const history = useHistory()
  const { userStore } = useStore()
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchText, 300)
  const [typeFilter, setTypeFilter] = useState<string | null>(null)
  const [cartridgeSpecs, setCartridgeSpecs] = useState<CartridgeSpecDto[]>([])
  const [loading, setLoading] = useState(false)
  const [activeCartridge, setActiveCartridge] = useState<CartridgeSpecDto | null>(null)
  const [ammoProducts, setAmmoProducts] = useState<AmmoProductDto[]>([])
  const [ammoLoading, setAmmoLoading] = useState(false)

  // Load cartridge specs
  useEffect(() => {
    setLoading(true)
    agent.CartridgeSpecs.list({
      searchText: debouncedSearch,
      type: typeFilter || undefined,
      pageNumber: 1,
      pageSize: 50
    })
      .then((result: PagedResult<CartridgeSpecDto>) => {
        setCartridgeSpecs(result.items)
      })
      .finally(() => setLoading(false))
  }, [debouncedSearch, typeFilter])

  // Load ammo products for selected cartridge
  useEffect(() => {
    if (!activeCartridge) {
      setAmmoProducts([])
      return
    }

    setAmmoLoading(true)
    agent.AmmoProducts.list({
      cartridgeSpecId: activeCartridge.id,
      pageNumber: 1,
      pageSize: 50
    })
      .then((result: PagedResult<AmmoProductDto>) => {
        setAmmoProducts(result.items)
      })
      .finally(() => setAmmoLoading(false))
  }, [activeCartridge])

  const canSubmit = userStore.user && userStore.user.ammoScore >= 10

  return (
    <Box sx={{ background: 'var(--gs-bg)', minHeight: '100vh' }}>
      {/* Header Bar */}
      <Box
        sx={{
          background: 'var(--gs-ammo-border)',
          padding: 'var(--gs-lg) 0',
          borderBottom: '2px solid var(--gs-border)',
          marginBottom: 'var(--gs-lg)'
        }}
      >
        <Container size="xl">
          <AppStack spacing={0}>
            <AppText
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontSize: '32px',
                fontWeight: 700,
                color: 'white',
                marginBottom: 'var(--gs-xs)'
              }}
            >
              AMMO REGISTRY
            </AppText>
            <AppText sx={{ color: 'var(--gs-muted)', fontSize: '14px', marginBottom: '12px' }}>
              Cartridge specifications and manufacturer loads.
            </AppText>
            <Group spacing="xs" mb="md">
              <AppButton
                variant="light"
                size="sm"
                onClick={() => {}}
                sx={{ cursor: 'default' }}
              >
                Specifications
              </AppButton>
              <AppButton
                variant="light"
                size="sm"
                onClick={() => history.push('/ammo/reloading')}
              >
                Reloader Forum
              </AppButton>
            </Group>
            {canSubmit && (
              <AppButton
                size="sm"
                sx={{ background: 'var(--gs-accent)', width: 'fit-content' }}
                onClick={() => history.push('/ammo/submit')}
              >
                + Submit Ammo
              </AppButton>
            )}
          </AppStack>
        </Container>
      </Box>

      <Container size="xl">
        <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
          {/* Left Panel - Cartridge Specs */}
          <Box>
            <AppStack spacing="md">
              <div>
                <AppText weight={700} size="lg" sx={{ marginBottom: 'var(--gs-sm)' }}>
                  Cartridge Specs
                </AppText>
                <AppInput
                  placeholder="Search by name or caliber..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.currentTarget.value)}
                  sx={{ marginBottom: '12px' }}
                />
                <Group spacing="xs" sx={{ marginBottom: '12px' }}>
                  <AppBadge
                    onClick={() => setTypeFilter(null)}
                    sx={{ cursor: 'pointer' }}
                    variant={typeFilter === null ? 'filled' : 'outline'}
                  >
                    All
                  </AppBadge>
                  {AMMO_TYPES.map((type) => (
                    <AppBadge
                      key={type}
                      onClick={() => setTypeFilter(type)}
                      sx={{ cursor: 'pointer' }}
                      variant={typeFilter === type ? 'filled' : 'outline'}
                    >
                      {type}
                    </AppBadge>
                  ))}
                </Group>
              </div>

              {loading
                ? (
                <Center>
                  <Loader />
                </Center>
                  )
                : (
                <AppStack spacing="sm">
                  {cartridgeSpecs.map((spec) => (
                    <CartridgeSpecCard
                      key={spec.id}
                      spec={spec}
                      isSelected={activeCartridge?.id === spec.id}
                      onClick={() => setActiveCartridge(spec)}
                    />
                  ))}
                </AppStack>
                  )}
            </AppStack>
          </Box>

          {/* Right Panel - Ammo Products */}
          <Box>
            {!activeCartridge
              ? (
              <Box
                sx={{
                  padding: '32px',
                  textAlign: 'center',
                  border: '1px dashed var(--gs-border)',
                  borderRadius: '6px'
                }}
              >
                <AppText sx={{ color: 'var(--gs-muted)' }}>
                  Select a cartridge to browse loads.
                </AppText>
              </Box>
                )
              : (
              <AppStack spacing="md">
                <div>
                  <AppText weight={700} size="lg" sx={{ marginBottom: '4px' }}>
                    {activeCartridge.name}
                  </AppText>
                  <AppText
                    size="xs"
                    sx={{
                      color: 'var(--gs-muted)',
                      fontFamily: 'JetBrains Mono, monospace'
                    }}
                  >
                    {activeCartridge.caliber} • {activeCartridge.type}
                  </AppText>
                </div>

                {ammoLoading
                  ? (
                  <Center>
                    <Loader />
                  </Center>
                    )
                  : ammoProducts.length === 0
                    ? (
                  <Box
                    sx={{
                      padding: '16px',
                      textAlign: 'center',
                      border: '1px dashed var(--gs-border)',
                      borderRadius: '6px'
                    }}
                  >
                    <AppText size="sm" sx={{ color: 'var(--gs-muted)' }}>
                      No loads for this cartridge yet.
                    </AppText>
                  </Box>
                      )
                    : (
                  <SimpleGrid cols={2} spacing="xs" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
                    {ammoProducts.map((product) => (
                      <AmmoProductCard
                        key={product.id}
                        product={product}
                        onClick={() => history.push(`/ammo/products/${product.id}`)}
                      />
                    ))}
                  </SimpleGrid>
                      )}
              </AppStack>
                )}
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
  )
})

export default AmmoDashboard
